// Copyright © 2026 ApexRuntime Labs by Shashi (https://github.com/spshah). All rights reserved.

import { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, AlertCircle, CreditCard } from 'lucide-react';
import paymentAPI from '../api/services/budget/paymentService';
import PaymentCardForm from '../components/payment/PaymentCardForm';
import PaymentModeForm from '../components/payment/PaymentModeForm';

export default function PaymentMethods() {
  const [cards, setCards] = useState([]);
  const [modes, setModes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('cards');
  const [showCardForm, setShowCardForm] = useState(false);
  const [showModeForm, setShowModeForm] = useState(false);
  const [editingCard, setEditingCard] = useState(null);
  const [editingMode, setEditingMode] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [cardsRes, modesRes] = await Promise.all([
        paymentAPI.listCards(),
        paymentAPI.listModes(),
      ]);
      setCards(cardsRes.data || []);
      setModes(modesRes.data || []);
    } catch (err) {
      setError('Failed to load payment methods');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Cards
  const handleCreateCard = async (data) => {
    try {
      const res = await paymentAPI.createCard(data);
      setCards([res.data, ...cards]);
      setShowCardForm(false);
      setEditingCard(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create card');
    }
  };

  const handleUpdateCard = async (data) => {
    try {
      const res = await paymentAPI.updateCard(editingCard.id, data);
      setCards(cards.map((c) => (c.id === editingCard.id ? res.data : c)));
      setShowCardForm(false);
      setEditingCard(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update card');
    }
  };

  const handleDeleteCard = async (id) => {
    if (!confirm('Delete this payment card?')) return;
    try {
      await paymentAPI.deleteCard(id);
      setCards(cards.filter((c) => c.id !== id));
    } catch (err) {
      setError('Failed to delete card');
    }
  };

  // Modes
  const handleCreateMode = async (data) => {
    try {
      const res = await paymentAPI.createMode(data);
      setModes([res.data, ...modes]);
      setShowModeForm(false);
      setEditingMode(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create payment mode');
    }
  };

  const handleUpdateMode = async (data) => {
    try {
      const res = await paymentAPI.updateMode(editingMode.id, data);
      setModes(modes.map((m) => (m.id === editingMode.id ? res.data : m)));
      setShowModeForm(false);
      setEditingMode(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update payment mode');
    }
  };

  const handleDeleteMode = async (id) => {
    if (!confirm('Delete this payment mode?')) return;
    try {
      await paymentAPI.deleteMode(id);
      setModes(modes.filter((m) => m.id !== id));
    } catch (err) {
      setError('Failed to delete payment mode');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">Loading payment methods…</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Payment Methods</h1>
      </div>

      {/* Error */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('cards')}
          className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${
            activeTab === 'cards'
              ? 'border-indigo-600 text-indigo-600'
              : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
        >
          Payment Cards
        </button>
        <button
          onClick={() => setActiveTab('modes')}
          className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${
            activeTab === 'modes'
              ? 'border-indigo-600 text-indigo-600'
              : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
        >
          Payment Modes
        </button>
      </div>

      {/* Cards Tab */}
      {activeTab === 'cards' && (
        <div className="space-y-4">
          <button
            onClick={() => {
              setEditingCard(null);
              setShowCardForm(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            <Plus size={18} />
            Add Card
          </button>

          {showCardForm && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg shadow-lg max-w-md w-full max-h-96 overflow-y-auto">
                <PaymentCardForm
                  initial={editingCard}
                  onSubmit={editingCard ? handleUpdateCard : handleCreateCard}
                  onClose={() => {
                    setShowCardForm(false);
                    setEditingCard(null);
                  }}
                />
              </div>
            </div>
          )}

          {cards.length === 0 ? (
            <div className="text-center py-12 border border-gray-200 rounded-lg">
              <CreditCard size={32} className="mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500">No payment cards added</p>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {cards.map((card) => (
                <div
                  key={card.id}
                  className="border border-gray-200 rounded-lg p-4 space-y-3"
                >
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-gray-900">{card.name}</h3>
                    <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
                      {card.card_type}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <p className="text-gray-500 text-xs">Bank</p>
                      <p className="font-medium text-gray-900">{card.bank}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-xs">Network</p>
                      <p className="font-medium text-gray-900">
                        {card.network}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-xs">Last 4 Digits</p>
                      <p className="font-medium text-gray-900">
                        •••• {card.last_four_digits}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-xs">Limit</p>
                      <p className="font-medium text-gray-900">
                        ₹{card.credit_limit?.toLocaleString() || 'N/A'}
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-2 justify-end pt-2">
                    <button
                      onClick={() => {
                        setEditingCard(card);
                        setShowCardForm(true);
                      }}
                      className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleDeleteCard(card.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Modes Tab */}
      {activeTab === 'modes' && (
        <div className="space-y-4">
          <button
            onClick={() => {
              setEditingMode(null);
              setShowModeForm(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            <Plus size={18} />
            Add Payment Mode
          </button>

          {showModeForm && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg shadow-lg max-w-md w-full max-h-96 overflow-y-auto">
                <PaymentModeForm
                  initial={editingMode}
                  cards={cards}
                  onSubmit={editingMode ? handleUpdateMode : handleCreateMode}
                  onClose={() => {
                    setShowModeForm(false);
                    setEditingMode(null);
                  }}
                />
              </div>
            </div>
          )}

          {modes.length === 0 ? (
            <div className="text-center py-12 border border-gray-200 rounded-lg">
              <CreditCard size={32} className="mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500">No payment modes added</p>
            </div>
          ) : (
            <div className="space-y-2">
              {modes.map((mode) => (
                <div
                  key={mode.id}
                  className="border border-gray-200 rounded-lg p-4 flex items-center justify-between"
                >
                  <div>
                    <h3 className="font-semibold text-gray-900">
                      {mode.name}
                    </h3>
                    <p className="text-sm text-gray-500">
                      Type: {mode.payment_type}
                      {mode.card_id && ' • Linked to card'}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setEditingMode(mode);
                        setShowModeForm(true);
                      }}
                      className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleDeleteMode(mode.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
